import type { FirebaseApp } from 'firebase/app';
import type { Auth, GoogleAuthProvider } from 'firebase/auth';
import type { Firestore } from 'firebase/firestore';

const firebaseConfig = {
    apiKey: 'AIzaSyCJ7D2R9y4UYVs0_Qo9hnY3Si5bgJeIXIs',
    authDomain: 'al-akhbar-2025.firebaseapp.com',
    projectId: 'al-akhbar-2025',
    storageBucket: 'al-akhbar-2025.firebasestorage.app',
    messagingSenderId: '463576263329',
    appId: '1:463576263329:web:4022ff73ab05f11f1d9ee0',
    measurementId: 'G-73E34NRSH0',
};

let app: FirebaseApp | undefined;
let appPromise: Promise<FirebaseApp> | undefined;
let authPromise: Promise<Auth> | undefined;
let dbPromise: Promise<Firestore> | undefined;
let providerPromise: Promise<GoogleAuthProvider> | undefined;

/**
 * Lazy Firebase app — `firebase/app` (and its `@firebase/util` dependency,
 * which ships un-transpiled `?.`/`??` syntax our build doesn't downlevel)
 * is only fetched once something actually needs Auth/Firestore, instead of
 * on every page. It used to be a static top-level import here, which made
 * every component that imports this module (even just for the lazy
 * `getFirebaseAuth`/`getFirebaseDb` helpers below) pull firebase/app in
 * eagerly — and Next's app-router bundled that into every page's required
 * scripts regardless, breaking parsing on old Safari / Samsung Internet
 * (PLATFORM-ALAKHBAR-12/13/W/V).
 */
function getFirebaseApp(): Promise<FirebaseApp> {
    if (app) return Promise.resolve(app);
    if (!appPromise) {
        appPromise = import('firebase/app').then(({ getApp, getApps, initializeApp }) => {
            app = getApps().length ? getApp() : initializeApp(firebaseConfig);
            return app;
        });
    }
    return appPromise;
}

/** Lazy Auth — loads `firebase/auth` only on first call (login / session restore). */
export function getFirebaseAuth(): Promise<Auth> {
    if (!authPromise) {
        authPromise = Promise.all([getFirebaseApp(), import('firebase/auth')]).then(
            ([firebaseApp, { getAuth }]) => getAuth(firebaseApp)
        );
    }
    return authPromise;
}

/** Lazy Google provider — same chunk as Auth. */
export function getGoogleProvider(): Promise<GoogleAuthProvider> {
    if (!providerPromise) {
        providerPromise = import('firebase/auth').then(({ GoogleAuthProvider }) => {
            return new GoogleAuthProvider();
        });
    }
    return providerPromise;
}

/** Lazy Firestore — loads only when bookmark / follow / lists need it. */
export function getFirebaseDb(): Promise<Firestore> {
    if (!dbPromise) {
        dbPromise = Promise.all([getFirebaseApp(), import('firebase/firestore')]).then(
            ([firebaseApp, { getFirestore }]) => getFirestore(firebaseApp)
        );
    }
    return dbPromise;
}
